<template>
  <div class="gs-source-row">
    <div v-if="!isYouTube" class="gs-source-row__legacy">
      <span class="gs-source-row__legacy-text">
        <span class="gs-source-row__legacy-label">{{ legacyLabel }}</span>
        <span class="gs-source-row__legacy-value">{{ isMux ? (model.mux_asset_id || 'Waiting for Mux') : model.video_source }}</span>
      </span>
      <gs-button
        v-if="!showField && !isMux"
        variant="outline"
        size="sm"
        data-action="switch"
        @click="revealField()"
      >
        Switch to a YouTube link
      </gs-button>
    </div>
    <p v-if="isMux && model.mux_error" class="gs-source-row__mux-error" role="alert">{{ model.mux_error }}</p>

    <gs-field
      v-if="isYouTube || showField"
      label="YouTube URL"
      hint="Use the link from YouTube's Share button. Unlisted videos work."
      :error="fieldError"
    >
      <div class="gs-source-row__field">
        <input
          data-field="source-url"
          type="url"
          inputmode="url"
          placeholder="https://youtu.be/…"
          class="gs-source-row__input"
          :class="{ 'gs-source-row__input--error': fieldError }"
          :value="url"
          @input="url = $event.target.value"
        />
        <gs-button
          variant="outline"
          data-action="replace"
          :disabled="!canReplace || saving"
          @click="askToReplace()"
        >
          Replace source
        </gs-button>
      </div>
    </gs-field>

    <!--
      The kit's `gs-alert-dialog` renders its own confirm button, so it cannot
      carry the `data-action` this row's contract is tested through. This is the
      same composition `gs-alert-dialog` itself uses — `gs-modal` at size `sm`
      with the alert-dialog copy and action classes — so the chrome is identical.
    -->
    <gs-modal
      class="gs-alert-dialog"
      :value="dialogOpen"
      title="Replace the video source?"
      size="sm"
      :close-on-backdrop="false"
      @input="dialogOpen = $event"
    >
      <p class="gs-alert-dialog__copy">
        Viewers will see the new video everywhere this player is embedded. Analytics are kept.
      </p>
      <div class="gs-alert-dialog__actions">
        <gs-button variant="outline" data-action="cancel-replace" @click="dialogOpen = false">
          {{ $t('frontend_cancel') }}
        </gs-button>
        <gs-button
          variant="primary"
          data-action="confirm-replace"
          :loading="saving"
          @click="replace()"
        >
          Replace source
        </gs-button>
      </div>
    </gs-modal>
  </div>
</template>

<script>
import SourceType from '@/models/SourceType';
import { isYouTubeUrl } from '@/utils/youtube';
import { isMuxVideo, muxStatusLabel } from '@/utils/muxSource';

export default {
  name: 'SourceRow',

  props: {
    model: {
      type: Object,
      required: true,
    },
    attachSource: {
      type: Function,
      default: payload => new SourceType(payload).save(),
    },
  },

  data() {
    return {
      url: Number(this.model.video_source_type) === 2 ? this.model.video_source || '' : '',
      showField: false,
      dialogOpen: false,
      saving: false,
      failure: '',
    };
  },

  computed: {
    isYouTube() {
      return Number(this.model.video_source_type) === 2;
    },

    isMux() {
      return isMuxVideo(this.model);
    },

    // Types are the API's: 1 is a file that was uploaded into Groove storage,
    // 3 is a link to a file hosted somewhere else, 4 is the owner's Mux account.
    legacyLabel() {
      if (this.isMux) {
        const status = muxStatusLabel(this.model.mux_status);
        return status ? `Mux · ${status}` : 'Mux';
      }
      return Number(this.model.video_source_type) === 3
        ? 'External link'
        : 'Legacy upload · stored in Groove storage';
    },

    fieldError() {
      if (this.failure) return this.failure;
      return this.url && !isYouTubeUrl(this.url) ? "That doesn't look like a YouTube link." : '';
    },

    canReplace() {
      const value = (this.url || '').trim();
      if (!isYouTubeUrl(value)) return false;
      return value !== (this.model.video_source || '').trim();
    },
  },

  watch: {
    // A re-fetch after a replacement (or an encoder poll) rewrites the source.
    'model.video_source': function onSourceChange(value) {
      if (this.isYouTube) this.url = value || '';
    },
  },

  methods: {
    revealField() {
      this.showField = true;
    },

    askToReplace() {
      if (!this.canReplace || this.saving) return;
      this.failure = '';
      this.dialogOpen = true;
    },

    async replace() {
      if (this.saving) return;
      this.saving = true;
      try {
        await this.attachSource({
          vid: this.model.id,
          type: 2,
          source: (this.url || '').trim(),
          name: this.model.name,
          category: this.model.category_id,
        });
        this.dialogOpen = false;
        this.showField = false;
        this.$emit('replaced');
      } catch (error) {
        this.dialogOpen = false;
        this.failure = 'We could not replace the video source. Please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.gs-source-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gs-source-row__legacy {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-source-row__legacy-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-source-row__legacy-label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-source-row__legacy-value {
  overflow-wrap: anywhere;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-source-row__field {
  display: flex;
  gap: 10px;
  align-items: center;
}

.gs-source-row__input {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 40px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font-size: 14px;
  line-height: 1.4;
}

.gs-source-row__input:focus {
  border-color: var(--gs-primary);
  outline: none;
  box-shadow: var(--gs-primary-shadow);
}

.gs-source-row__input--error {
  border-color: var(--gs-danger);
}

@media (max-width: 640px) {
  .gs-source-row__legacy,
  .gs-source-row__field {
    flex-direction: column;
    align-items: stretch;
  }
}

.gs-source-row__mux-error { margin: 8px 0 0; color: var(--gs-danger); font-size: 13px; }
</style>
